import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Empty, Input, Modal, Select } from "antd";
import { checkoutModalControlsOverrideCss } from "utils/checkoutTheme";
const { Option, OptGroup } = Select;

/** Country picker Modal renders via portal outside `.main-container-card`; theme it through this own class. */
const COUNTRY_MODAL_SCOPE = ".country-mobile-modal";

// Resolves the country display name for the active UI language.
// Driven by i18n keys (`countries.<key>`) so new languages only need a locale
// entry; falls back to englishName -> localName -> value when a key is missing
// or when `t` is unavailable (e.g. React Fast Refresh during local dev).
const getCountryName = (country, t) => {
  const fallbackName = country.englishName || country.localName || country.value;

  if (typeof t !== "function") {
    return fallbackName;
  }

  return t(`countries.${country.key}`, { defaultValue: fallbackName });
};

const buildGeoOptions = (countries = [], t) => {
  return countries.flatMap((country) => {
    const countryName = getCountryName(country, t);
    return country.regions.map((region) => {
      const isSingleRegion = country.regions.length === 1;
      const label = isSingleRegion ? countryName : `${countryName} - ${region.name}`;
      const key = `${country.value}-${region.value}`;

      return {
        key,
        countryCode: country.value,
        regionCode: region.value,
        countryName,
        regionName: region.name,
        label,
        flagSprite: country.flagSprite,
        flag: country.flag,
        // Extra names so search matches regardless of the active UI language.
        localName: country.localName,
        englishName: country.englishName,
        portugueseName: country.portugueseName,
      };
    });
  });
};

const selectRegionOptions = (country, t) => {
  const countryName = getCountryName(country, t);
  return country.regions.map((region, j) => (
    <Option key={j} value={`${country.value}-${region.value}`} label={`${countryName} - ${region.name}`}>
      <span className={`flag-sprite-small flag-sprite-${country.flagSprite}`} style={{ marginRight: 5 }}>
        {country.flag}
      </span>
      {region.name}
    </Option>
  ));
};

const flagAndName = (country, t) => (
  <>
    <span className={`flag-sprite flag-sprite-${country.flagSprite}`} style={{ marginRight: 5 }}>
      {country.flag}
    </span>
    {getCountryName(country, t)}
  </>
);

const SelectGeoLocationOptions = ({ countries, selectedCountry, selectedRegion, onChange }) => {
  const { t } = useTranslation();
  const selectionKey = `${selectedCountry.value}-${selectedRegion.value}`;
  const optionsList = countries.map((country, i) => {
    if (country.regions.length === 1) {
      const key = `${country.value}-${country.regions[0].value}`;
      return (
        <Option key={key} value={key} label={getCountryName(country, t)}>
          {flagAndName(country, t)}
        </Option>
      );
    } else {
      return (
        <OptGroup key={`group${i}`} label={flagAndName(country, t)}>
          {selectRegionOptions(country, t)}
        </OptGroup>
      );
    }
  });

  return (
    <Select
      style={{ minWidth: 150 }}
      optionLabelProp="label"
      value={selectionKey}
      onChange={(e) => {
        const [country, region] = e.split("-");
        onChange(country, region);
      }}
    >
      {optionsList}
    </Select>
  );
};

const withAlpha = (hexColor, alpha) => {
  if (typeof hexColor !== "string") return null;
  const normalized = hexColor.trim().replace("#", "");
  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) return null;
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const MobileGeoLocationSelector = ({
  countries,
  selectedCountry,
  selectedRegion,
  onChange,
  customStyle,
  modalTitle,
  searchPlaceholder,
  closeText,
  noResultsText,
}) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [search, setSearch] = useState("");
  const [pressingOptionKey, setPressingOptionKey] = useState(null);
  const [hoveredOptionKey, setHoveredOptionKey] = useState(null);
  const searchInputRef = useRef(null);

  // Focus the search field when the modal opens. Antd 4 Modal keeps content
  // mounted across reopens, so `autoFocus` only fires on first open; a small
  // delay lets the open animation finish before focusing.
  useEffect(() => {
    if (!visible) return;
    const timer = setTimeout(() => searchInputRef.current?.focus(), 100);
    return () => clearTimeout(timer);
  }, [visible]);

  const allOptions = useMemo(() => buildGeoOptions(countries, t), [countries, t]);
  const selectionKey = `${selectedCountry.value}-${selectedRegion.value}`;
  const selectedOption = allOptions.find((option) => option.key === selectionKey);
  const selectedBorderColor = customStyle?.link?.linkColor || customStyle?.primary?.backgroundColor || "#1890ff";
  const selectedBackgroundColor = withAlpha(selectedBorderColor, 0.15) || "#e6f7ff";
  const pressingBorderColor = withAlpha(selectedBorderColor, 0.9) || selectedBorderColor;
  const pressingBackgroundColor = withAlpha(selectedBorderColor, 0.08) || "#f5f5f5";
  const hoverBorderColor = withAlpha(selectedBorderColor, 0.7) || selectedBorderColor;
  const hoverBackgroundColor = withAlpha(selectedBorderColor, 0.06) || "#f5f5f5";

  const filteredOptions = useMemo(() => {
    const searchText = search.trim().toLowerCase();
    if (!searchText) return allOptions;

    return allOptions.filter((option) => {
      return (
        option.label.toLowerCase().includes(searchText) ||
        option.countryName.toLowerCase().includes(searchText) ||
        option.regionName.toLowerCase().includes(searchText) ||
        (option.localName || "").toLowerCase().includes(searchText) ||
        (option.englishName || "").toLowerCase().includes(searchText) ||
        (option.portugueseName || "").toLowerCase().includes(searchText)
      );
    });
  }, [allOptions, search]);

  const onSelectOption = (option) => {
    onChange(option.countryCode, option.regionCode);
    setVisible(false);
    setSearch("");
  };

  const modalThemeCss = checkoutModalControlsOverrideCss(COUNTRY_MODAL_SCOPE, customStyle);

  // Same accent resolution as LanguageSelector so both controls share the border color.
  const triggerAccent = customStyle?.primary?.backgroundColor || customStyle?.link?.linkColor || "#9aa6b6";

  return (
    <>
      {modalThemeCss ? <style>{modalThemeCss}</style> : null}
      <Button className="country-mobile-trigger" style={{ "--lang-accent": triggerAccent }} onClick={() => setVisible(true)}>
        <span className="country-mobile-trigger-label">{selectedOption?.label || `${getCountryName(selectedCountry, t)} - ${selectedRegion.name}`}</span>
        <span className="country-mobile-trigger-icon" />
      </Button>

      <Modal
        className="country-mobile-modal"
        visible={visible}
        title={modalTitle}
        style={{ top: 20 }}
        onCancel={() => setVisible(false)}
        footer={[
          <Button key="close" onClick={() => setVisible(false)}>
            {closeText}
          </Button>,
        ]}
      >
        <div className="country-mobile-search-wrap">
          <Input
            ref={searchInputRef}
            className="country-mobile-search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={searchPlaceholder}
            allowClear
          />
        </div>
        <div className="country-mobile-list">
          {filteredOptions.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={noResultsText} />}
          {filteredOptions.map((option) => {
            const isActive = option.key === selectionKey;
            const isPressing = pressingOptionKey === option.key;
            const isHovered = hoveredOptionKey === option.key;

            let optionStyle;
            if (isActive) {
              optionStyle = {
                borderColor: selectedBorderColor,
                background: selectedBackgroundColor,
              };
            } else if (isPressing) {
              optionStyle = {
                borderColor: pressingBorderColor,
                background: pressingBackgroundColor,
              };
            } else if (isHovered) {
              optionStyle = {
                borderColor: hoverBorderColor,
                background: hoverBackgroundColor,
              };
            }

            return (
              <button
                type="button"
                key={option.key}
                className={`country-mobile-option ${isActive ? "country-mobile-option-selected" : ""} ${
                  isPressing ? "country-mobile-option-pressing" : ""
                }`}
                style={optionStyle}
                onTouchStart={() => setPressingOptionKey(option.key)}
                onTouchEnd={() => setPressingOptionKey(null)}
                onTouchCancel={() => setPressingOptionKey(null)}
                onMouseDown={() => setPressingOptionKey(option.key)}
                onMouseUp={() => setPressingOptionKey(null)}
                onMouseEnter={() => setHoveredOptionKey(option.key)}
                onMouseLeave={() => {
                  setPressingOptionKey(null);
                  setHoveredOptionKey(null);
                }}
                onBlur={() => setHoveredOptionKey(null)}
                onClick={() => onSelectOption(option)}
              >
                <span className={`flag-sprite flag-sprite-${option.flagSprite}`} style={{ marginRight: 8 }}>
                  {option.flag}
                </span>
                <span>{option.label}</span>
              </button>
            );
          })}
        </div>
      </Modal>
    </>
  );
};

export default SelectGeoLocationOptions;
